import { resolvePostLoginTarget as getPostLoginTarget } from '../../utils/roleWorkspace';
import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../components/ui/notify';
import { validateEmail, validatePassword, validateForm } from '../../utils/validation';
import { loginApi, sendActiveAccountOtpFromLoginApi } from '../../services/auth/authService';
import { usePermissions } from '../../context/PermissionsContext';
import { useOrganizationBranding } from '../../hooks/useOrganizationBranding';
import { AuthOrgBrandingLogo } from '../../components/auth/AuthOrgBrandingLogo';
import {
  ensureWebPushRegistration,
  resetWebPushPromptState,
} from '../../services/webPush/firebaseWebPush';
import { persistUserTimeZone } from '../../utils/timezone';
import { getAuthApiErrorMessage } from './authApiError';
import { isFeedFeatureEnabled } from '../../utils/featureFlags';

function resolveLandingPageUrl(url) {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function resolvePostLoginTarget(role, permissions) {
  const pendingRedirect = sessionStorage.getItem('post_login_redirect');
  if (pendingRedirect) sessionStorage.removeItem('post_login_redirect');
  return getPostLoginTarget(role, permissions, pendingRedirect);
}

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const orgBranding = useOrganizationBranding('Login', { refreshFromApi: true });
  const navigate = useNavigate();
  const location = useLocation();
  const { setPermissionsFromAPI } = usePermissions();

  const showLandingPageLink =
    Boolean(orgBranding?.landing_page_enabled) && Boolean(orgBranding?.landing_page_url);

  const handleBackToLandingPage = () => {
    const landingUrl = resolveLandingPageUrl(orgBranding?.landing_page_url);
    if (!landingUrl) return;
    window.location.href = landingUrl;
  };

  useEffect(() => {
    const routeMessage = location.state?.authMessage;
    if (routeMessage) {
      toast.info(routeMessage, { position: "top-center", autoClose: 5000 });
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    const redirectedMessage = sessionStorage.getItem('auth_redirect_message');
    if (redirectedMessage) {
      toast.error(redirectedMessage, { position: "top-center", autoClose: 5000 });
      sessionStorage.removeItem('auth_redirect_message');
    }
  }, [location.pathname, location.state, navigate]);

  // Validation Rules
  const validationRules = {
    email: validateEmail,
    password: validatePassword
  };

  // Handle Change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update both form data and clear errors in a single function
    const newFormData = { ...formData, [name]: value };
    const newErrors = { ...errors, [name]: undefined };

    setFormData(newFormData);
    setErrors(newErrors);
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = formData.email.trim().toLowerCase();
    const validation = validateForm({ ...formData, email: normalizedEmail }, validationRules);
    if (validation.isValid) {
      setFormData((current) => ({ ...current, email: normalizedEmail }));
      setIsLoading(true);
      const loaderStart = Date.now();
      const payload = {
        email: normalizedEmail,
        password: formData.password,
        organization_url: window.location.origin,
        browser_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      };

      try {
        const response = await loginApi(payload);

        // ⛔ Email not verified - redirect to OTP page
        if (response?.data?.is_email_verified === 0) {
          try {
            const otpPayload = { email: normalizedEmail, domain: window.location.origin };
            await sendActiveAccountOtpFromLoginApi(otpPayload);
            let elapsed = Date.now() - loaderStart;
            if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));
            toast.success(response?.message, {
              position: "top-center",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              onClose: () => {
                setIsLoading(false);
                navigate('/auth/active-account', { state: { email: normalizedEmail } });
              }
            });
          } catch (otpError) {
            console.error("[Login] Send OTP error:", otpError);
            toast.error(getAuthApiErrorMessage(otpError), { position: "top-center", autoClose: 3000 });
            setIsLoading(false);
          }
          return;
        }

        let elapsed = Date.now() - loaderStart;
        if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));

        if (response.success) {
          if (response?.data?.requires_2fa === 1) {
            toast.info('Enter the 2FA code sent to your email.', {
              position: "top-center",
              autoClose: 1500,
              onClose: () => {
                setIsLoading(false);
                navigate('/auth/login-2fa', { state: { email: normalizedEmail } });
              }
            });
            return;
          }

          // Set permissions in context
          setPermissionsFromAPI(response.data.permissions);

          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);
          localStorage.setItem('user', JSON.stringify(response.data.user_details));
          persistUserTimeZone(response.data.user_details?.time_zone);
          localStorage.setItem('organization_details', JSON.stringify(response.data.dashboard));
          localStorage.setItem('role', response.data.role);

          resetWebPushPromptState();
          try {
            await ensureWebPushRegistration({ requestPermission: true });
          } catch (pushError) {
            console.warn('[Login] Web push registration failed:', pushError);
          }

          toast.success(response.data?.message ?? response.message, {
            position: "top-center",
            autoClose: 1000,
            onClose: () => {
              navigate(resolvePostLoginTarget(response.data.role, response.data.permissions));
            }
          });
        } else {
          if (response?.message) toast.error(response.message, { position: "top-center", autoClose: 5000 });
          setIsLoading(false);
        }
      } catch (error) {
        console.error("[Login] API error:", error?.response?.data ?? error);
        let elapsed = Date.now() - loaderStart;
        if (elapsed < 800) await new Promise(r => setTimeout(r, 800 - elapsed));
        toast.error(getAuthApiErrorMessage(error), { position: "top-center", autoClose: 5000 });
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };



  return (
    <div className={"max-[480px]:tw-p-4 [[data-theme='dark']_&]:tw-bg-[#1a1b1e] tw-min-h-[100dvh] tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-[2rem_1rem] tw-bg-[#F5F7FF] [background:radial-gradient(ellipse_at_20%_0%,_#e8edff_0%,_transparent_45%),_#f5f7fb] auth-container"}>
      <div className={"max-[480px]:tw-p-6 [[data-theme='dark']_&]:tw-bg-[#25262b] [[data-theme='dark']_&]:tw-shadow-[0px_4px_20px_rgba(0,_0,_0,_0.15)] tw-relative tw-bg-[#FFFFFF] tw-rounded-[1.25rem] tw-p-10 tw-w-full tw-max-w-[28.75rem] tw-shadow-[0_1rem_3rem_-1.5rem_rgb(30_41_59_/_.25)] tw-border-[1px] tw-border-solid tw-border-[#e2e8f0] max-[767px]:tw-p-[1.75rem_1.5rem] auth-card"}>
        {showLandingPageLink ? (
          <button
            type="button"
            className={"tw-absolute tw-top-5 tw-left-5 tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-p-0 tw-border-0 tw-rounded-[.375rem] tw-bg-[transparent] tw-text-[#475467] tw-cursor-pointer [&:hover]:tw-bg-[#F2F4F7] [&:hover]:tw-text-[#101828] [&:focus-visible]:[outline:2px_solid_var(--primary-color)] [&:focus-visible]:[outline-offset:2px] auth-back-icon"}
            onClick={handleBackToLandingPage}
            aria-label="Back to landing page"
            title="Back"
          >
            <Icon icon="mdi:arrow-left" width={20} height={20} aria-hidden="true" />
          </button>
        ) : null}
        <div className={"tw-text-center tw-mb-8 [&_h2]:tw-text-[#101828] [&_h2]:tw-text-[1.5625rem] [&_h2]:[font-weight:650] [&_h2]:tw-leading-[2rem] [&_h2]:tw-m-[0_0_.5rem_0] [&_h2]:tw-mt-6 [&_h2]:tw-tracking-[-.0375rem] [&_p]:tw-text-[#64748b] [&_p]:tw-text-[.875rem] [&_p]:tw-leading-[1.5rem] [&_p]:tw-m-0 [&_img]:tw-max-h-[4rem] [&_img]:tw-object-contain auth-header"}>
          <AuthOrgBrandingLogo orgBranding={orgBranding} />
          <h2>{orgBranding?.name ? `Login to ${orgBranding.name}` : 'Login to LMS'}</h2>
          <p>Enter your email & password to login...</p>
        </div>

        <form onSubmit={handleSubmit} className={"[&_.ui-button-primary]:tw-mt-[var(--spacing-xs)] [&_.ui-button-primary]:tw-mb-[var(--spacing-xs)] tw-flex tw-flex-col tw-gap-[var(--spacing-md)] auth-form"}>
          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="email">Email Address</label>
            <div className="tw-relative tw-flex tw-items-center auth-input-wrapper">
              <Icon icon="mdi:email-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
              <input
                type="text"
                id="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com"
                className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.email ? "error" : ""}`}
                disabled={isLoading}
              />
            </div>
            {errors.email && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.email[0]}</span>}
          </div>

          <div className={"tw-relative tw-flex tw-flex-col tw-gap-1.5 tw-min-h-[4.5rem] tw-mb-4 [&_label]:tw-text-[#344054] [&_label]:tw-text-[.875rem] [&_label]:tw-font-medium [&_label]:tw-leading-[1.25rem] auth-form-group"}>
            <label htmlFor="password">Password</label>
            <div className="tw-relative tw-flex tw-items-center auth-input-wrapper">
              <Icon icon="mdi:lock-outline" className={"tw-absolute tw-left-[.875rem] tw-text-[#667085] tw-text-[1.25rem] [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.5)] auth-input-icon"} />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••"
                className={`[&.error:focus]:tw-border-[var(--error-border)] [&.error:focus]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&::placeholder]:tw-text-[#667085] [[data-theme='dark']_&::placeholder]:tw-text-[var(--text-light)] [[data-theme='dark']_&]:[background:rgba(255,_255,_255,_0.05)] [[data-theme='dark']_&]:tw-border-[rgba(255,_255,_255,_0.1)] [[data-theme='dark']_&]:tw-text-[white] [[data-theme='dark']_&:focus]:[background:rgba(255,_255,_255,_0.08)] [[data-theme='dark']_&:focus]:tw-border-[#4361ee] tw-w-full tw-h-[2.75rem] tw-p-[.625rem_.875rem_.625rem_2.625rem] tw-border-[1px] tw-border-solid tw-border-[#dbe1eb] tw-rounded-[.625rem] tw-bg-[#fff] tw-text-[1rem] tw-leading-[1.5rem] tw-text-[#101828] [transition:all_0.15s_ease-in-out] tw-min-h-[2.875rem] [font-family:inherit] [&:focus]:tw-border-[#5265e8] [&:focus]:tw-shadow-[0_0_0_3px_#eef2ff] [&:focus]:[outline:none] [&.error]:tw-border-[#fbbf24] [&.error]:tw-shadow-[0_0_0_4px_rgba(253,_162,_155,_0.1)] [&.error]:tw-bg-[#fffbeb] auth-input   ${errors.password ? "error" : ""}`}
                disabled={isLoading}
              />
              <button
                type="button"
                className={"tw-absolute tw-right-[.875rem] tw-bg-[none] tw-border-0 tw-p-0 tw-text-[#667085] tw-cursor-pointer tw-flex tw-items-center tw-justify-center [&:hover]:tw-text-[#101828] [[data-theme='dark']_&]:tw-text-[var(--text-light)] [[data-theme='dark']_&:hover]:tw-text-[var(--text-primary)] auth-password-toggle"}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={isLoading}
              >
                <Icon icon={showPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} />
              </button>
            </div>
            {errors.password && <span className="tw-text-[#92400e] tw-text-[.75rem] tw-mt-[4px] tw-block auth-error-message">{errors.password[0]}</span>}
          </div>

          <div className={"[&_a:hover]:tw-text-[#4361ee] tw-text-right tw-m-0 tw-p-0 tw-mt-[-.5rem] [&_a]:tw-text-[var(--primary-color)] [&_a]:tw-text-[.875rem] [&_a]:tw-no-underline [&_a]:[transition:color_0.2s_ease] [&_a]:tw-inline-block [&_a]:tw-p-[0.25rem_0] [&_a]:tw-leading-[1.25rem] [[data-theme='dark']_&_a]:tw-text-[rgba(255,_255,_255,_0.6)] auth-forgot-password"}>
            <a href="/auth/forgot-password" className={isLoading ? "tw-opacity-[0.6] tw-pointer-events-none disabled-link" : ""}>Forgot Password?</a>
          </div>

          <div className={"tw-flex tw-flex-col tw-gap-0 [&_.ui-button-primary]:tw-mb-0 [&_.ui-button]:tw-w-full [&_.ui-button]:tw-min-h-[2.875rem] [&_.ui-button]:tw-rounded-[.625rem] [&_.ui-button]:tw-shadow-[none] [&_.auth-submit-button.loading:disabled]:!tw-bg-[#4054d6] [&_.auth-submit-button.loading:disabled]:!tw-text-[#fff] [&_.auth-submit-button.loading:disabled]:!tw-opacity-[.82] [&_.auth-submit-button.loading:disabled:hover]:!tw-bg-[#4054d6] [&_.auth-submit-button.loading:disabled:hover]:!tw-text-[#fff] [&_.auth-submit-button.loading:disabled:hover]:!tw-opacity-[.82] [&_.auth-submit-button.loading:disabled_.spinner]:tw-border-[rgb(255_255_255_/_55%)] [&_.auth-submit-button.loading:disabled_.spinner]:tw-border-t-[#fff] auth-form-footer"}>
            <button
              type="submit"
              className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary auth-submit-button ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="tw-w-[20px] tw-h-[20px] tw-border-[2px] tw-border-solid tw-border-[rgba(255,_255,_255,_0.3)] tw-rounded-[50%] tw-border-t-[#fff] tw-animate-[spin_0.8s_linear_infinite] tw-mr-[8px] tw-inline-block spinner" aria-hidden="true"></span>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>

            {isFeedFeatureEnabled() && (
              <div className={"tw-flex tw-items-center tw-justify-center tw-gap-1 tw-mt-[var(--spacing-xs)] tw-text-center tw-text-[#667085] tw-text-[.875rem] tw-leading-[1.25rem] [&_a]:tw-text-[var(--primary-color)] [&_a]:tw-no-underline [&_a]:tw-font-medium [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.6)] [[data-theme='dark']_&_a]:tw-text-[#4895ef] auth-options auth-register-prompt"}>
                <span>Don't have an account?</span>
                <Link to="/auth/register" className={isLoading ? "tw-opacity-[0.6] tw-pointer-events-none disabled-link" : ""}>Register</Link>
              </div>
            )}

            <div className={"tw-flex tw-items-center tw-justify-center tw-flex-wrap tw-gap-3 [&_a]:tw-leading-[1.4] [&_a]:tw-m-0 [&_a]:tw-p-0 [&_a_+_a]:tw-border-l-[1px] [&_a_+_a]:tw-border-solid [&_a_+_a]:tw-border-l-[#d0d5dd] [&_a_+_a]:tw-pl-3 tw-mt-[var(--spacing-xs)] tw-text-center tw-text-[#667085] tw-text-[.875rem] tw-leading-[1.25rem] [&_a]:tw-text-[var(--primary-color)] [&_a]:tw-no-underline [&_a]:tw-font-medium [[data-theme='dark']_&]:tw-text-[rgba(255,_255,_255,_0.6)] [[data-theme='dark']_&_a]:tw-text-[#4895ef] auth-options auth-footer-links"}>
              <Link to="/privacy">Privacy Policy</Link>
              {showLandingPageLink ? (
                <a
                  href={resolveLandingPageUrl(orgBranding.landing_page_url)}
                  className={isLoading ? "tw-opacity-[0.6] tw-pointer-events-none disabled-link" : ""}
                >
                  Home
                </a>
              ) : null}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
